import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Renovation/Expansion Content) ---
import {
    // Core Concepts & General
    faTools,            // Renovation/Re-modelling
    faExpandArrowsAlt,  // Expansion
    faBuilding,         // Facility/Infrastructure
    faUsersCog,         // Team/Experts/Stakeholders
    faLightbulb,        // Vision/Innovation
    faCheckCircle,      // Compliance/Quality/Approval/Handover
    // Why Renovate Reasons
    faUsers,            // Patient Demand
    faRoute,            // Patient Flow/Traffic (Alternative: faArrowsAltH)
    faChartLine,        // Strategic Growth
    faLaptopCode,       // Technological Advancements
    // faCheckCircle used again for Compliance
    faTrophy,           // Competitive Pressure (Alternative: faMedal)
    faWarehouse,        // Utilizing Space / Efficiency (Alternative: faBolt)
    // Process Steps
    faSearch,           // Assessment/Consultation
    faDraftingCompass,  // Design Development/Planning
    faComments,         // Stakeholder Review
    faDollarSign,       // Budgeting/Resource Planning
    faHardHat,          // Project Execution/Construction
    // faClipboardCheck used again for Post-Renovation Assessment/Handover
    // Why Choose Cliniqor
    faUserTie,          // Expertise (Healthcare specific)
    faCog,              // Tailored Solutions
    faTasks,            // Comprehensive Services
    faLeaf,             // Sustainability
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faSearchPlus, faHospital, faShieldAlt, faAward, faProjectDiagram, faPalette,
    faDirections, faRulerCombined, faBiohazard, faMicroscope
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Renovate/Expand?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Approach (Process)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for renovation/construction
    const heroBgImage = 'https://images.unsplash.com/photo-1581092917219-05f80e6091ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Reusing construction image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Re-Modelling, Renovation, and Expansion Services</h1>
            <p className="cliniqor-sub-heading">
              Comprehensive Healthcare Facility Transformation by Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor specializes in expert hospital renovation, expansion, and re-modelling to enhance existing infrastructure, meet growing demands, and deliver end-to-end solutions that elevate operations and improve patient outcomes.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION (Optional Elaboration) --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid">
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>Evolving Healthcare Infrastructure</h3>
                     <p>Our team of consultants, architects, and design experts provide tailored solutions aligned with your vision and future needs. From small-scale renovations to full-scale expansions, we guide you through the entire process, ensuring your hospital remains competitive, efficient, and future-ready.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY RENOVATE/EXPAND? (Features Section - 7 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Hospital Renovation, Expansion, or Re-Modelling Is Imperative</h2>
              <p>Addressing key challenges and opportunities in the evolving healthcare landscape.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Adjusted grid for potentially 7 items - ideally requires CSS adjustment */}
            <style>{`.cliniqor-features-grid { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); } @media (min-width: 992px) { .cliniqor-features-grid { grid-template-columns: repeat(4, 1fr); } }`}</style> {/* Example: 4 cols on large screens */}
            <div className="cliniqor-features-grid">
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div>
                    <h4>1. Growing Patient Demand</h4>
                    <p>Accommodating more patients by adding departments, equipment, or specialized units.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faRoute} /></div>
                    <h4>2. Increasing Patient Flow & Traffic</h4>
                    <p>Enhancing capacity and streamlining operations to reduce bottlenecks and wait times.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                    <h4>3. Strategic Growth & Development</h4>
                    <p>Aligning facility expansion with long-term goals for capacity and competitiveness.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <h4>4. Technological Advancements</h4>
                    <p>Updating facilities to incorporate state-of-the-art tech like robotics and telemedicine.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <h4>5. Addressing Regulatory Compliance</h4>
                    <p>Meeting new accreditation standards, building codes, or patient safety regulations.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTrophy} /></div>
                    <h4>6. Competitive Pressure</h4>
                    <p>Improving infrastructure and tech to maintain a competitive edge in the local market.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faWarehouse} /></div>
                    <h4>7. Utilizing Space / Increasing Efficiency</h4>
                    <p>Transforming underused areas or inefficient layouts to optimize workflows and reduce costs.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- CLINIQOR'S APPROACH (Detailed Planning Section - 6 steps) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Cliniqor’s Comprehensive Approach to Renovation, Re-Modelling, and Expansion</h2>
               <p>A detailed, step-by-step process ensuring flawless execution with minimal disruption.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Process Steps */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSearch} /></div>
                    <div className="cliniqor-content"><h4>1. Initial Consultation & Comprehensive Assessment</h4> <p>Identifying pain points, goals, and evaluating existing infrastructure, tech, flow, and space use.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDraftingCompass} /></div>
                    <div className="cliniqor-content"><h4>2. Strategic Planning & Design Development</h4> <p>Creating master plans, innovative designs, incorporating tech/sustainability, and ensuring compliance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faComments} /></div>
                    <div className="cliniqor-content"><h4>3. Stakeholder Review and Input</h4> <p>Sharing designs, incorporating feedback from management/clinicians, refining plans.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <div className="cliniqor-content"><h4>4. Budgeting, Resource Planning & Approval</h4> <p>Detailing costs, assisting with funding options, establishing timelines and milestones.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHardHat} /></div>
                    <div className="cliniqor-content"><h4>5. Project Execution & Construction Management</h4> <p>Overseeing contractors, ensuring quality control, managing timelines, ensuring safety/compliance.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCheckCircle} /></div> {/* Using check for final step */}
                    <div className="cliniqor-content"><h4>6. Post-Renovation Assessment & Handover</h4> <p>Conducting final quality checks, facility walkthrough, providing training and support.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR? (Reusing Features Layout - 4 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Why Choose Cliniqor for Your Hospital Renovation Project?</h2>
                 <p>Delivering expertise, tailored solutions, and comprehensive support.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Why Choose --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                            <h4>1. Expertise in Healthcare Infrastructure</h4>
                            <p>Proven track record with specialized consultants, architects, and engineers.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCog} /></div>
                            <h4>2. Tailored, Future-Ready Solutions</h4>
                            <p>Custom designs incorporating patient-centricity, tech, flexibility, and adaptability.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                            <h4>3. Comprehensive Services</h4>
                            <p>End-to-end support from initial planning to execution and final handover.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLeaf} /></div>
                            <h4>4. Commitment to Sustainability</h4>
                            <p>Prioritizing energy efficiency, green materials, and sustainable practices.</p>
                        </div>
                    </div>
                    {/* Grid suited for 4 items. Add empty placeholders or adjust CSS if needed */}
               </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Ready to Transform Your Healthcare Facility?</h3>
          <p>If you are ready to expand, renovate, or re-model your hospital, Cliniqor is your trusted partner. Contact us today to discuss your project and build the future of healthcare together, enhancing efficiency and patient care.</p>
          <a href="#contact" className="cliniqor-cta-button">Discuss Your Renovation Needs</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;